import { useState, useMemo, useCallback } from 'react'
import { useDashReport } from '../hooks/useDashReport'

interface PaisItem {
  pais: string
  total: number
  en_base: number
  especialidades: [string, number][]
  num_especialidades: number
}

interface MarcaItem {
  marca: string
  total: number
  total_en_base: number
  paises: PaisItem[]
  num_paises: number
}

interface OpellaDashboardData {
  titulo: string
  subtitulo: string
  total_general: number
  total_en_base: number
  porcentaje_en_base: number
  total_marcas: number
  total_paises: number
  total_especialidades: number
  marcas: MarcaItem[]
  lista_paises: string[]
}

export function OpellaDashboardPage() {
  const { data, isLoading, error } = useDashReport<OpellaDashboardData>('opella/dashboard')
  const [filtroMarca, setFiltroMarca] = useState('todas')
  const [filtroPais, setFiltroPais] = useState('todos')
  const [expandedEsp, setExpandedEsp] = useState<Record<string, boolean>>({})

  // Compute available options for each filter based on the other
  const marcasDisponibles = useMemo(() => {
    if (!data) return []
    if (filtroPais === 'todos') return data.marcas
    return data.marcas.filter(m => m.paises.some(p => p.pais === filtroPais))
  }, [data, filtroPais])

  const paisesDisponibles = useMemo(() => {
    if (!data) return [] as string[]
    if (filtroMarca === 'todas') return data.lista_paises
    const marca = data.marcas.find(m => m.marca === filtroMarca)
    if (!marca) return data.lista_paises
    return marca.paises.map(p => p.pais).sort()
  }, [data, filtroMarca])

  // Compute summary stats based on filters
  const resumen = useMemo(() => {
    if (!data) return { totalMedicos: 0, enBase: 0, porcentaje: 0, marcas: 0, paises: 0, especialidades: 0 }
    if (filtroMarca === 'todas' && filtroPais === 'todos') {
      return {
        totalMedicos: data.total_general,
        enBase: data.total_en_base,
        porcentaje: data.porcentaje_en_base,
        marcas: data.total_marcas,
        paises: data.total_paises,
        especialidades: data.total_especialidades,
      }
    }

    let totalMedicos = 0
    let totalEnBase = 0
    const marcasSet = new Set<string>()
    const paisesSet = new Set<string>()
    const especialidadesSet = new Set<string>()

    data.marcas.forEach(marca => {
      if (filtroMarca !== 'todas' && marca.marca !== filtroMarca) return
      let marcaTienePais = false

      marca.paises.forEach(paisData => {
        if (filtroPais !== 'todos' && paisData.pais !== filtroPais) return
        marcaTienePais = true
        totalMedicos += paisData.total
        totalEnBase += paisData.en_base || 0
        paisesSet.add(paisData.pais)
        paisData.especialidades.forEach(esp => {
          especialidadesSet.add(esp[0])
        })
      })

      if (marcaTienePais) marcasSet.add(marca.marca)
    })

    const porcentaje = totalMedicos > 0 ? parseFloat(((totalEnBase / totalMedicos) * 100).toFixed(1)) : 0

    return {
      totalMedicos,
      enBase: totalEnBase,
      porcentaje,
      marcas: marcasSet.size,
      paises: paisesSet.size,
      especialidades: especialidadesSet.size,
    }
  }, [data, filtroMarca, filtroPais])

  const hayFiltrosActivos = filtroMarca !== 'todas' || filtroPais !== 'todos'

  const handleMarcaChange = useCallback((value: string) => {
    setFiltroMarca(value)
    // Reset pais if not available in new marca
    if (value !== 'todas' && data) {
      const marca = data.marcas.find(m => m.marca === value)
      if (marca && filtroPais !== 'todos') {
        const paisDisponible = marca.paises.some(p => p.pais === filtroPais)
        if (!paisDisponible) setFiltroPais('todos')
      }
    }
  }, [data, filtroPais])

  const handlePaisChange = useCallback((value: string) => {
    setFiltroPais(value)
    // Reset marca if not available in new pais
    if (value !== 'todos' && data) {
      if (filtroMarca !== 'todas') {
        const marca = data.marcas.find(m => m.marca === filtroMarca)
        if (marca && !marca.paises.some(p => p.pais === value)) {
          setFiltroMarca('todas')
        }
      }
    }
  }, [data, filtroMarca])

  const limpiarFiltros = useCallback(() => {
    setFiltroMarca('todas')
    setFiltroPais('todos')
  }, [])

  const toggleEspecialidades = useCallback((key: string) => {
    setExpandedEsp(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Determine which marca cards to show
  const marcasFiltradas = useMemo(() => {
    if (!data) return []
    return data.marcas.filter(marca => {
      if (filtroMarca !== 'todas' && marca.marca !== filtroMarca) return false
      if (filtroPais !== 'todos' && !marca.paises.some(p => p.pais === filtroPais)) return false
      return true
    })
  }, [data, filtroMarca, filtroPais])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const today = new Date()
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  const fechaGenerado = `${today.getDate()} de ${meses[today.getMonth()]} de ${today.getFullYear()}`

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://docs.google.com/spreadsheets/d/1hEgjnFAEI2dBXuOG63jjldY1sRGBADq9/edit?usp=sharing&ouid=113144325878788930414&rtpof=true&sd=true"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Tabla
                <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-6h8v-2H8v2zm0 4h8v-2H8v2zm0-8h5V8H8v2z" />
                </svg>
              </a>
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>
          {/* Primera fila: Médicos */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{resumen.totalMedicos.toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{resumen.enBase.toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-1">En Base</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-teal-600">{resumen.porcentaje}%</p>
              <p className="text-sm text-gray-600 mt-1">Porcentaje</p>
            </div>
          </div>
          {/* Segunda fila: Distribución */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-indigo-600">{resumen.marcas}</p>
              <p className="text-sm text-gray-600 mt-1">Marcas</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{resumen.paises}</p>
              <p className="text-sm text-gray-600 mt-1">Países</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-purple-600">{resumen.especialidades}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros
            </h3>
            {hayFiltrosActivos && (
              <button
                onClick={limpiarFiltros}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpiar filtros
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filtro de Marca */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Marca
              </label>
              <select
                value={filtroMarca}
                onChange={e => handleMarcaChange(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              >
                <option value="todas">Todas las marcas</option>
                {marcasDisponibles.map(marca => (
                  <option key={marca.marca} value={marca.marca}>
                    {marca.marca} ({marca.total} médicos)
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de País */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                País
              </label>
              <select
                value={filtroPais}
                onChange={e => handlePaisChange(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
              >
                <option value="todos">Todos los países</option>
                {paisesDisponibles.map((pais: string) => (
                  <option key={pais} value={pais}>{pais}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Cards de Marcas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {marcasFiltradas.map(marca => {
            const paisesVisibles = filtroPais === 'todos'
              ? marca.paises
              : marca.paises.filter(p => p.pais === filtroPais)

            return (
              <div
                key={marca.marca}
                className="bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                {/* Header de la marca */}
                <div className="bg-indigo-600 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">{marca.marca}</h3>
                    <div className="flex items-center gap-3">
                      <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {marca.total} médicos
                      </span>
                      {marca.total_en_base > 0 && (
                        <span className="bg-green-500/30 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {marca.total_en_base} en base
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                    <span>{marca.num_paises} países</span>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-4">
                  {paisesVisibles.map((pais, paisIdx) => {
                    const espKey = `${marca.marca}-${pais.pais}`
                    const isExpanded = expandedEsp[espKey] || false
                    const top5 = pais.especialidades.slice(0, 5)
                    const rest = pais.especialidades.slice(5)

                    return (
                      <div key={pais.pais}>
                        <div className="mb-4 last:mb-0">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-green-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {pais.pais}
                            </h4>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-indigo-600">{pais.total} médicos</span>
                              {pais.en_base > 0 && (
                                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                  {pais.en_base} en base
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Top 5 Especialidades */}
                          <div className="space-y-2">
                            {top5.map(([esp, cantidad]) => (
                              <div key={esp} className="flex items-center">
                                <span className="text-xs text-gray-600 w-32 truncate" title={esp}>{esp}</span>
                                <div className="flex-1 mx-2 bg-gray-100 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${pais.total > 0 ? Math.round((cantidad / pais.total) * 100) : 0}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-gray-700 w-10 text-right">{cantidad}</span>
                              </div>
                            ))}
                          </div>

                          {rest.length > 0 && (
                            <>
                              <button
                                onClick={() => toggleEspecialidades(espKey)}
                                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
                              >
                                <span>{isExpanded ? 'Ver menos' : `Ver ${rest.length} más`}</span>
                                <svg
                                  className={`w-3 h-3 ml-1 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isExpanded && (
                                <div className="mt-2 space-y-2">
                                  {rest.map(([esp, cantidad]) => (
                                    <div key={esp} className="flex items-center">
                                      <span className="text-xs text-gray-600 w-32 truncate" title={esp}>{esp}</span>
                                      <div className="flex-1 mx-2 bg-gray-100 rounded-full h-2 overflow-hidden">
                                        <div
                                          className="bg-indigo-400 h-2 rounded-full transition-all duration-500"
                                          style={{ width: `${pais.total > 0 ? Math.round((cantidad / pais.total) * 100) : 0}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-medium text-gray-700 w-10 text-right">{cantidad}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {paisIdx < paisesVisibles.length - 1 && (
                          <hr className="my-4 border-gray-200" />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mt-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>2026 Tonal Strategies - Dashboard de Análisis</p>
          <p className="mt-2">Generado: {fechaGenerado}</p>
        </div>
      </div>
    </div>
  )
}
